/**
 * Session admin — `useState` + `useContext`, rien de plus.
 *
 * Pas de bibliothèque de state : il y a exactement deux informations à
 * partager, le jeton et l'email, et une seule transition (connecté /
 * déconnecté).
 *
 * La connexion se fait en plusieurs pas (cf. shared/types/auth.ts) : chaque
 * fonction renvoie le pas suivant, et la session n'est écrite que sur `ok`.
 * C'est la page de connexion qui tient l'état intermédiaire (ticket, QR
 * code) : il n'a rien à faire dans un contexte global.
 */

import type { AdminRole, LoginStep } from '@shared/types/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { changePasswordWithTicket, fetchAccount, login as loginRequest, verifyMfa as verifyMfaRequest } from '../lib/api';
import { clearSession, readDeviceToken, readSession, writeDeviceToken, writeSession } from '../lib/auth';

interface AuthState {
  token: string | null;
  email: string | null;
  /**
   * Rôle du compte (cf. shared/types/auth.ts) ; `null` le temps de le relire
   * pour une session ouverte avant les rôles. Sert au menu et aux routes —
   * l'API, elle, applique le rôle quoi qu'il arrive.
   */
  role: AdminRole | null;
  isAuthenticated: boolean;
  /** Mot de passe. Renvoie le pas suivant ; la session est ouverte sur `ok`. */
  signIn: (email: string, password: string) => Promise<LoginStep>;
  /** Nouveau mot de passe imposé à la connexion. */
  completePasswordChange: (ticket: string, newPassword: string) => Promise<LoginStep>;
  /** Code du second facteur (enrôlement ou vérification). */
  completeMfa: (ticket: string, code: string, rememberDevice: boolean) => Promise<LoginStep>;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Initialiseur paresseux : localStorage n'est lu qu'au premier rendu, pas a
  // chaque re-rendu du provider.
  const [session, setSession] = useState(() => readSession());

  /** Sur `ok`, ouvre la session (et retient l'appareil si l'API le dit). */
  const settle = useCallback((step: LoginStep): LoginStep => {
    if (step.step === 'ok') {
      if (step.deviceToken) writeDeviceToken(step.deviceToken);
      const next = { token: step.token, email: step.admin.email, role: step.admin.role };
      writeSession(next);
      setSession(next);
    }
    return step;
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => settle(await loginRequest(email, password, readDeviceToken())),
    [settle],
  );

  const completePasswordChange = useCallback(
    async (ticket: string, newPassword: string) => settle(await changePasswordWithTicket(ticket, newPassword)),
    [settle],
  );

  const completeMfa = useCallback(
    async (ticket: string, code: string, rememberDevice: boolean) => settle(await verifyMfaRequest(ticket, code, rememberDevice)),
    [settle],
  );

  const signOut = useCallback(() => {
    clearSession();
    setSession(null);
  }, []);

  // Session d'avant les rôles : on relit le rôle une fois.
  const token = session?.token ?? null;
  const missingRole = session !== null && session.role === null;
  useEffect(() => {
    if (!token || !missingRole) return;
    const controller = new AbortController();
    fetchAccount(token, controller.signal)
      .then((account) => {
        setSession((current) => {
          if (!current || current.token !== token) return current;
          const next = { ...current, role: account.admin.role };
          writeSession(next);
          return next;
        });
      })
      .catch((error: unknown) => {
        // Rôle illisible : on se reconnecte plutôt que de rester sur une page
        // blanche (la connexion renvoie le rôle).
        if (error instanceof DOMException && error.name === 'AbortError') return;
        signOut();
      });
    return () => controller.abort();
  }, [token, missingRole, signOut]);

  const value = useMemo<AuthState>(
    () => ({
      token: session?.token ?? null,
      email: session?.email ?? null,
      role: session?.role ?? null,
      isAuthenticated: session !== null,
      signIn,
      completePasswordChange,
      completeMfa,
      signOut,
    }),
    [session, signIn, completePasswordChange, completeMfa, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un <AuthProvider>');
  }
  return context;
}
